import _ from 'lodash'

import {euCountriesCodes, Locale} from '@/utils/constants'
import {Region} from '@/utils/countries'
import {Jurisdiction} from '@/utils/countries/constants'
import {getJurisdictionsAlpha2} from '@/utils/countries/helpers'
import {alpha2ToName} from '@/utils/countries/translations'
import {jurisdictionsData} from '@/utils/jurisdictions/constants'

type CountryLandingPageData = {
  imageSrc: string
  imageAlt: string
  isIceCream?: boolean
}

type CountryCardContent = {
  key: Jurisdiction
  imageSrc: string
  imageAlt: string
  tooltipText: string
  shortTooltipText?: string
  renewalTooltip?: string
  dataTest: string
  landingPageData?: CountryLandingPageData
  slug: string
}

export type CountryCard = CountryCardContent & {
  title: string
}

export const countryCards: Record<Jurisdiction, CountryCardContent> = {
  AD: {
    key: Jurisdiction.AD,
    imageSrc: '/images/flags/ad-flag.svg',
    imageAlt: 'Flag of Andorra',
    tooltipText: 'offering-card-tooltip-text-2-4',
    dataTest: 'adCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/andorra.svg',
      imageAlt: 'Ice cream with the flag of Andorra',
      isIceCream: true,
    },
    slug: 'andorra',
  },
  AE: {
    key: Jurisdiction.AE,
    imageSrc: '/images/flags/ae-flag.svg',
    imageAlt: 'Flag of United Arab Emirates',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'aeCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/united-arab-emirates.svg',
      imageAlt: 'Ice cream with the flag of United Arab Emirates',
      isIceCream: true,
    },
    slug: 'united-arab-emirates',
  },
  AM: {
    key: Jurisdiction.AM,
    imageSrc: '/images/flags/am-flag.svg',
    imageAlt: 'Flag of Armenia',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'amCountryCard',
    slug: 'armenia',
  },
  AO: {
    key: Jurisdiction.AO,
    imageSrc: '/images/flags/ao-flag.svg',
    imageAlt: 'Flag of Angola',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'aoCountryCard',
    slug: 'angola',
  },
  AR: {
    key: Jurisdiction.AR,
    imageSrc: '/images/flags/ar-flag.svg',
    imageAlt: 'Flag of Argentina',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'arCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/argentina.svg',
      imageAlt: 'Ice cream with the flag of Argentina',
      isIceCream: true,
    },
    slug: 'argentina',
  },
  AP: {
    key: Jurisdiction.AP,
    imageSrc: '/images/flags/ap-flag.svg',
    imageAlt: 'Logo of ARIPO',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'aripoCountryCard',
    slug: 'aripo',
  },
  AT: {
    key: Jurisdiction.AT,
    imageSrc: '/images/flags/at-flag.svg',
    imageAlt: 'Flag of Austria',
    tooltipText: 'offering-card-tooltip-text-2-4',
    dataTest: 'atCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/austria.svg',
      imageAlt: 'Ice cream with the flag of Austria',
      isIceCream: true,
    },
    slug: 'austria',
  },
  AU: {
    key: Jurisdiction.AU,
    imageSrc: '/images/flags/au-flag.svg',
    imageAlt: 'Flag of Australia',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'auCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/sydney.svg',
      imageAlt: 'Sydney Opera House with Sydney Tower, Australia',
    },
    slug: 'australia',
  },
  AZ: {
    key: Jurisdiction.AZ,
    imageSrc: '/images/flags/az-flag.svg',
    imageAlt: 'Flag of Azerbaijan',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'azCountryCard',
    slug: 'azerbaijan',
  },
  BA: {
    key: Jurisdiction.BA,
    imageSrc: '/images/flags/ba-flag.svg',
    imageAlt: 'Flag of Bosnia and Herzegovina',
    tooltipText: 'offering-card-tooltip-text-2-4',
    shortTooltipText: 'offering-card-tooltip-text-2-4-short',
    dataTest: 'baCountryCard',
    slug: 'bosnia-and-herzegovina',
  },
  BD: {
    key: Jurisdiction.BD,
    imageSrc: '/images/flags/bd-flag.svg',
    imageAlt: 'Flag of Bangladesh',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'bdCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/bangladesh.svg',
      imageAlt: 'Ice cream with the flag of Bangladesh',
      isIceCream: true,
    },
    slug: 'bangladesh',
  },
  BG: {
    key: Jurisdiction.BG,
    imageSrc: '/images/flags/bg-flag.svg',
    imageAlt: 'Flag of Bulgaria',
    tooltipText: 'offering-card-tooltip-text-2-4',
    shortTooltipText: 'offering-card-tooltip-text-2-4-short',
    dataTest: 'bgCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/bulgaria.svg',
      imageAlt: 'Ice cream with the flag of Bulgaria',
      isIceCream: true,
    },
    slug: 'bulgaria',
  },
  BH: {
    key: Jurisdiction.BH,
    imageSrc: '/images/flags/bh-flag.svg',
    imageAlt: 'Flag of Bahrain',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'bhCountryCard',
    slug: 'bahrain',
  },
  BI: {
    key: Jurisdiction.BI,
    imageSrc: '/images/flags/bi-flag.svg',
    imageAlt: 'Flag of Burundi',
    tooltipText: 'offering-card-tooltip-text-2-4',
    dataTest: 'biCountryCard',
    slug: 'burundi',
  },
  BO: {
    key: Jurisdiction.BO,
    imageSrc: '/images/flags/bo-flag.svg',
    imageAlt: 'Flag of Bolivia',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'boCountryCard',
    slug: 'bolivia',
  },
  BR: {
    key: Jurisdiction.BR,
    imageSrc: '/images/flags/br-flag.svg',
    imageAlt: 'Flag of Brazil',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'brCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/brazil.svg',
      imageAlt: 'Christ the Redeemer Statue, Toucan, Carnival, Rio de Janeiro, Brazil',
    },
    slug: 'brazil',
  },
  BX: {
    key: Jurisdiction.BX,
    imageSrc: '/images/flags/bx-flag.svg',
    imageAlt: 'Flag of Benelux',
    tooltipText: 'offering-card-tooltip-text-2-3',
    shortTooltipText: 'offering-card-tooltip-text-2-3-short',
    dataTest: 'bxCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/benelux.svg',
      imageAlt: 'Ice cream with the flag of Benelux',
      isIceCream: true,
    },
    slug: 'benelux',
  },
  CA: {
    key: Jurisdiction.CA,
    imageSrc: '/images/flags/ca-flag.svg',
    imageAlt: 'Flag of Canada',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'caCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/toronto.svg',
      imageAlt: 'Toronto skyline, Canada',
    },
    slug: 'canada',
  },
  CD: {
    key: Jurisdiction.CD,
    imageSrc: '/images/flags/cd-flag.svg',
    imageAlt: 'Flag of Congo',
    tooltipText: 'offering-card-tooltip-text-2-4',
    dataTest: 'cdCountryCard',
    slug: 'congo',
  },
  CH: {
    key: Jurisdiction.CH,
    imageSrc: '/images/flags/ch-flag.svg',
    imageAlt: 'Flag of Switzerland',
    tooltipText: 'offering-card-tooltip-text-2-4',
    shortTooltipText: 'offering-card-tooltip-text-2-4-short',
    dataTest: 'chCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/switzerland.svg',
      imageAlt: 'Matterhorn, village in Switzerland',
    },
    slug: 'switzerland',
  },
  CL: {
    key: Jurisdiction.CL,
    imageSrc: '/images/flags/cl-flag.svg',
    imageAlt: 'Flag of Chile',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'clCountryCard',
    slug: 'chile',
  },
  CN: {
    key: Jurisdiction.CN,
    imageSrc: '/images/flags/cn-flag.svg',
    imageAlt: 'Flag of China',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'cnCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/china.svg',
      imageAlt: 'China',
    },
    slug: 'china',
  },
  CO: {
    key: Jurisdiction.CO,
    imageSrc: '/images/flags/co-flag.svg',
    imageAlt: 'Flag of Colombia',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'coCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/colombia.svg',
      imageAlt: 'Ice cream with the flag of Colombia',
      isIceCream: true,
    },
    slug: 'colombia',
  },
  CV: {
    key: Jurisdiction.CV,
    imageSrc: '/images/flags/cv-flag.svg',
    imageAlt: 'Flag of Cape Verde',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'cvCountryCard',
    slug: 'cape-verde',
  },
  CY: {
    key: Jurisdiction.CY,
    imageSrc: '/images/flags/cy-flag.svg',
    imageAlt: 'Flag of Cyprus',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'cyCountryCard',
    slug: 'cyprus',
  },
  CZ: {
    key: Jurisdiction.CZ,
    imageSrc: '/images/flags/cz-flag.svg',
    imageAlt: 'Flag of the Czech Republic',
    tooltipText: 'offering-card-tooltip-text-2-4',
    shortTooltipText: 'offering-card-tooltip-text-2-4-short',
    dataTest: 'czCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/prague.svg',
      imageAlt: 'Charles Bridge and the Church of Our Lady before Tyn, Prague, Czech Republic',
    },
    slug: 'czech-republic',
  },
  DE: {
    key: Jurisdiction.DE,
    imageSrc: '/images/flags/de-flag.svg',
    imageAlt: 'Flag of Germany',
    tooltipText: 'offering-card-tooltip-text-2-4',
    shortTooltipText: 'offering-card-tooltip-text-2-4-short',
    dataTest: 'deCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/germany.svg',
      imageAlt: 'Brandenburg Gate, Television Tower, Victory Column in Berlin, Germany',
    },
    slug: 'germany',
  },
  DJ: {
    key: Jurisdiction.DJ,
    imageSrc: '/images/flags/dj-flag.svg',
    imageAlt: 'Flag of Djibouti',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'djCountryCard',
    slug: 'djibouti',
  },
  DK: {
    key: Jurisdiction.DK,
    imageSrc: '/images/flags/dk-flag.svg',
    imageAlt: 'Flag of Denmark',
    tooltipText: 'offering-card-tooltip-text-2-3',
    shortTooltipText: 'offering-card-tooltip-text-2-3-short',
    dataTest: 'dkCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/denmark.svg',
      imageAlt: 'Ice cream with the flag of Denmark',
      isIceCream: true,
    },
    slug: 'denmark',
  },
  DZ: {
    key: Jurisdiction.DZ,
    imageSrc: '/images/flags/dz-flag.svg',
    imageAlt: 'Flag of Algeria',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'dzCountryCard',
    slug: 'algeria',
  },
  EC: {
    key: Jurisdiction.EC,
    imageSrc: '/images/flags/ec-flag.svg',
    imageAlt: 'Flag of Ecuador',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'ecCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/ecuador.svg',
      imageAlt: 'Ice cream with the flag of Ecuador',
      isIceCream: true,
    },
    slug: 'ecuador',
  },
  EE: {
    key: Jurisdiction.EE,
    imageSrc: '/images/flags/ee-flag.svg',
    imageAlt: 'Flag of Estonia',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'eeCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/estonia.svg',
      imageAlt: 'Ice cream with the flag of Estonia',
      isIceCream: true,
    },
    slug: 'estonia',
  },
  EG: {
    key: Jurisdiction.EG,
    imageSrc: '/images/flags/eg-flag.svg',
    imageAlt: 'Flag of Egypt',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'egCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/egypt.svg',
      imageAlt: 'Ice cream with the flag of Egypt',
      isIceCream: true,
    },
    slug: 'egypt',
  },
  ER: {
    key: Jurisdiction.ER,
    imageSrc: '/images/flags/er-flag.svg',
    imageAlt: 'Flag of Eritrea',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'erCountryCard',
    slug: 'eritrea',
  },
  ES: {
    key: Jurisdiction.ES,
    imageSrc: '/images/flags/es-flag.svg',
    imageAlt: 'Flag of Spain',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'esCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/spain.svg',
      imageAlt: 'Ice cream with the flag of Spain',
      isIceCream: true,
    },
    slug: 'spain',
  },
  ET: {
    key: Jurisdiction.ET,
    imageSrc: '/images/flags/et-flag.svg',
    imageAlt: 'Flag of Ethiopia',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'etCountryCard',
    slug: 'ethiopia',
  },
  EU: {
    key: Jurisdiction.EU,
    imageSrc: '/images/flags/eu-flag.svg',
    imageAlt: 'Flag of the European Union',
    tooltipText: 'offering-card-tooltip-text-2-3',
    shortTooltipText: 'offering-card-tooltip-text-2-3-short',
    renewalTooltip: 'offering-card-tooltip-text-2-3',
    dataTest: 'euCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/european-union.svg',
      imageAlt: 'Brandenburg Gate, Eiffel tower, Colosseum, Leaning Tower of Pisa',
    },
    slug: 'european-union',
  },
  FI: {
    key: Jurisdiction.FI,
    imageSrc: '/images/flags/fi-flag.svg',
    imageAlt: 'Flag of Finland',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'fiCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/finland.svg',
      imageAlt: 'Ice cream with the flag of Finland',
      isIceCream: true,
    },
    slug: 'finland',
  },
  FR: {
    key: Jurisdiction.FR,
    imageSrc: '/images/flags/fr-flag.svg',
    imageAlt: 'Flag of France',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'frCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/france.svg',
      imageAlt: 'Ice cream with the flag of France',
      isIceCream: true,
    },
    slug: 'france',
  },
  GE: {
    key: Jurisdiction.GE,
    imageSrc: '/images/flags/ge-flag.svg',
    imageAlt: 'Flag of Georgia',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'geCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/georgia.svg',
      imageAlt: 'Ice cream with the flag of Georgia',
      isIceCream: true,
    },
    slug: 'georgia',
  },
  GH: {
    key: Jurisdiction.GH,
    imageSrc: '/images/flags/gh-flag.svg',
    imageAlt: 'Flag of Ghana',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'ghCountryCard',
    slug: 'ghana',
  },
  HK: {
    key: Jurisdiction.HK,
    imageSrc: '/images/flags/hk-flag.svg',
    imageAlt: 'Flag of Hong Kong',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'hkCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/hong-kong.svg',
      imageAlt: 'Ice cream with the flag of Hong Kong',
      isIceCream: true,
    },
    slug: 'hong-kong',
  },
  HR: {
    key: Jurisdiction.HR,
    imageSrc: '/images/flags/hr-flag.svg',
    imageAlt: 'Flag of Croatia',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'hrCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/croatia.svg',
      imageAlt: 'Ice cream with the flag of Croatia',
      isIceCream: true,
    },
    slug: 'croatia',
  },
  HU: {
    key: Jurisdiction.HU,
    imageSrc: '/images/flags/hu-flag.svg',
    imageAlt: 'Flag of Hungary',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'huCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/hungary.svg',
      imageAlt: 'Ice cream with the flag of Hungary',
      isIceCream: true,
    },
    slug: 'hungary',
  },
  ID: {
    key: Jurisdiction.ID,
    imageSrc: '/images/flags/id-flag.svg',
    imageAlt: 'Flag of Indonesia',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'idCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/indonesia.svg',
      imageAlt: 'Ice cream with the flag of Indonesia',
      isIceCream: true,
    },
    slug: 'indonesia',
  },
  IE: {
    key: Jurisdiction.IE,
    imageSrc: '/images/flags/ie-flag.svg',
    imageAlt: 'Flag of Ireland',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'ieCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/ireland.svg',
      imageAlt: 'Ice cream with the flag of Ireland',
      isIceCream: true,
    },
    slug: 'ireland',
  },
  IL: {
    key: Jurisdiction.IL,
    imageSrc: '/images/flags/il-flag.svg',
    imageAlt: 'Flag of Israel',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'ilCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/israel.svg',
      imageAlt: 'Ice cream with the flag of Israel',
      isIceCream: true,
    },
    slug: 'israel',
  },
  IN: {
    key: Jurisdiction.IN,
    imageSrc: '/images/flags/in-flag.svg',
    imageAlt: 'Flag of India',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'inCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/india.svg',
      imageAlt: 'Taj Mahal, India',
    },
    slug: 'india',
  },
  IS: {
    key: Jurisdiction.IS,
    imageSrc: '/images/flags/is-flag.svg',
    imageAlt: 'Flag of Iceland',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'isCountryCard',
    slug: 'iceland',
  },
  IT: {
    key: Jurisdiction.IT,
    imageSrc: '/images/flags/it-flag.svg',
    imageAlt: 'Flag of Italy',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'itCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/italy.svg',
      imageAlt: 'Ice cream with the flag of Italy',
      isIceCream: true,
    },
    slug: 'italy',
  },
  JO: {
    key: Jurisdiction.JO,
    imageSrc: '/images/flags/jo-flag.svg',
    imageAlt: 'Flag of Jordan',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'joCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/jordan.svg',
      imageAlt: 'Ice cream with the flag of Jordan',
      isIceCream: true,
    },
    slug: 'jordan',
  },
  JP: {
    key: Jurisdiction.JP,
    imageSrc: '/images/flags/jp-flag.svg',
    imageAlt: 'Flag of Japan',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'jpCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/japan.svg',
      imageAlt: 'Fushimi inari taisha shrine, Pagoda, Mount Fuji, Cherry blossoms in Japan',
    },
    slug: 'japan',
  },
  KE: {
    key: Jurisdiction.KE,
    imageSrc: '/images/flags/ke-flag.svg',
    imageAlt: 'Flag of Kenya',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'keCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/kenya.svg',
      imageAlt: 'Ice cream with the flag of Kenya',
      isIceCream: true,
    },
    slug: 'kenya',
  },
  KG: {
    key: Jurisdiction.KG,
    imageSrc: '/images/flags/kg-flag.svg',
    imageAlt: 'Flag of Kyrgyzstan',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'kgCountryCard',
    slug: 'kyrgyzstan',
  },
  KH: {
    key: Jurisdiction.KH,
    imageSrc: '/images/flags/kh-flag.svg',
    imageAlt: 'Flag of Cambodia',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'khCountryCard',
    slug: 'cambodia',
  },
  KR: {
    key: Jurisdiction.KR,
    imageSrc: '/images/flags/kr-flag.svg',
    imageAlt: 'Flag of South Korea',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'krCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/south-korea.svg',
      imageAlt: 'Ice cream with the flag of South Korea',
      isIceCream: true,
    },
    slug: 'south-korea',
  },
  KW: {
    key: Jurisdiction.KW,
    imageSrc: '/images/flags/kw-flag.svg',
    imageAlt: 'Flag of Kuwait',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'kwCountryCard',
    slug: 'kuwait',
  },
  KZ: {
    key: Jurisdiction.KZ,
    imageSrc: '/images/flags/kz-flag.svg',
    imageAlt: 'Flag of Kazakhstan',
    tooltipText: 'offering-card-tooltip-text-2-4',
    shortTooltipText: 'offering-card-tooltip-text-2-4-short',
    dataTest: 'kzCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/kazakhstan.svg',
      imageAlt: 'Ice cream with the flag of Kazakhstan',
      isIceCream: true,
    },
    slug: 'kazakhstan',
  },
  LA: {
    key: Jurisdiction.LA,
    imageSrc: '/images/flags/la-flag.svg',
    imageAlt: 'Flag of Laos',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'laCountryCard',
    slug: 'laos',
  },
  LB: {
    key: Jurisdiction.LB,
    imageSrc: '/images/flags/lb-flag.svg',
    imageAlt: 'Flag of Lebanon',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'lbCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/lebanon.svg',
      imageAlt: 'Ice cream with the flag of Lebanon',
      isIceCream: true,
    },
    slug: 'lebanon',
  },
  LT: {
    key: Jurisdiction.LT,
    imageSrc: '/images/flags/lt-flag.svg',
    imageAlt: 'Flag of Lithuania',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'ltCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/lithuania.svg',
      imageAlt: 'Ice cream with the flag of Lithuania',
      isIceCream: true,
    },
    slug: 'lithuania',
  },
  LV: {
    key: Jurisdiction.LV,
    imageSrc: '/images/flags/lv-flag.svg',
    imageAlt: 'Flag of Latvia',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'lvCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/latvia.svg',
      imageAlt: 'Ice cream with the flag of Latvia',
      isIceCream: true,
    },
    slug: 'latvia',
  },
  MA: {
    key: Jurisdiction.MA,
    imageSrc: '/images/flags/ma-flag.svg',
    imageAlt: 'Flag of Morocco',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'maCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/morocco.svg',
      imageAlt: 'Ice cream with the flag of Morocco',
      isIceCream: true,
    },
    slug: 'morocco',
  },
  MC: {
    key: Jurisdiction.MC,
    imageSrc: '/images/flags/mc-flag.svg',
    imageAlt: 'Flag of Monaco',
    tooltipText: 'offering-card-tooltip-text-2-4',
    dataTest: 'mcCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/monaco.svg',
      imageAlt: 'Ice cream with the flag of Monaco',
      isIceCream: true,
    },
    slug: 'monaco',
  },
  MD: {
    key: Jurisdiction.MD,
    imageSrc: '/images/flags/md-flag.svg',
    imageAlt: 'Flag of Moldova',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'mdCountryCard',
    slug: 'moldova',
  },
  ME: {
    key: Jurisdiction.ME,
    imageSrc: '/images/flags/me-flag.svg',
    imageAlt: 'Flag of Montenegro',
    tooltipText: 'offering-card-tooltip-text-2-4',
    dataTest: 'meCountryCard',
    slug: 'montenegro',
  },
  MG: {
    key: Jurisdiction.MG,
    imageSrc: '/images/flags/mg-flag.svg',
    imageAlt: 'Flag of Madagascar',
    tooltipText: 'offering-card-tooltip-text-2-4',
    dataTest: 'mgCountryCard',
    slug: 'madagascar',
  },
  MU: {
    key: Jurisdiction.MU,
    imageSrc: '/images/flags/mu-flag.svg',
    imageAlt: 'Flag of Mauritius',
    tooltipText: 'offering-card-tooltip-text-2-3',
    dataTest: 'muCountryCard',
    slug: 'mauritius',
  },
  MX: {
    key: Jurisdiction.MX,
    imageSrc: '/images/flags/mx-flag.svg',
    imageAlt: 'Flag of Mexico',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'mxCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/mexico.svg',
      imageAlt:
        'Cactus, Guitar, Sugar skull, Tequila with Lime, Chili peppers, Yucatan, Mayan pyramid, Mexico',
    },
    slug: 'mexico',
  },
  MY: {
    key: Jurisdiction.MY,
    imageSrc: '/images/flags/my-flag.svg',
    imageAlt: 'Flag of Malaysia',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'myCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/malaysia.svg',
      imageAlt: 'Ice cream with the flag of Malaysia',
      isIceCream: true,
    },
    slug: 'malaysia',
  },
  NG: {
    key: Jurisdiction.NG,
    imageSrc: '/images/flags/ng-flag.svg',
    imageAlt: 'Flag of Nigeria',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'ngCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/nigeria.svg',
      imageAlt: 'Ice cream with the flag of Nigeria',
      isIceCream: true,
    },
    slug: 'nigeria',
  },
  NO: {
    key: Jurisdiction.NO,
    imageSrc: '/images/flags/no-flag.svg',
    imageAlt: 'Flag of Norway',
    tooltipText: 'offering-card-tooltip-text-2-4',
    shortTooltipText: 'offering-card-tooltip-text-2-4-short',
    dataTest: 'noCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/norway.svg',
      imageAlt: 'Lighthouse, Viking ship, Colourful houses in Bergen, Norway',
    },
    slug: 'norway',
  },
  NZ: {
    key: Jurisdiction.NZ,
    imageSrc: '/images/flags/nz-flag.svg',
    imageAlt: 'Flag of New Zealand',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'nzCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/new-zealand.svg',
      imageAlt: 'Ice cream with the flag of New Zealand',
      isIceCream: true,
    },
    slug: 'new-zealand',
  },
  OA: {
    key: Jurisdiction.OA,
    imageSrc: '/images/flags/oa-flag.svg',
    imageAlt: 'Logo of OAPI',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'oapiCountryCard',
    slug: 'oapi',
  },
  OM: {
    key: Jurisdiction.OM,
    imageSrc: '/images/flags/om-flag.svg',
    imageAlt: 'Flag of Oman',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'omCountryCard',
    slug: 'oman',
  },
  PA: {
    key: Jurisdiction.PA,
    imageSrc: '/images/flags/pa-flag.svg',
    imageAlt: 'Flag of Panama',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'paCountryCard',
    slug: 'panama',
  },
  PE: {
    key: Jurisdiction.PE,
    imageSrc: '/images/flags/pe-flag.svg',
    imageAlt: 'Flag of Peru',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'peCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/peru.svg',
      imageAlt: 'Ice cream with the flag of Peru',
      isIceCream: true,
    },
    slug: 'peru',
  },
  PH: {
    key: Jurisdiction.PH,
    imageSrc: '/images/flags/ph-flag.svg',
    imageAlt: 'Flag of Philippines',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'phCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/philippines.svg',
      imageAlt: 'Ice cream with the flag of Philippines',
      isIceCream: true,
    },
    slug: 'philippines',
  },
  PK: {
    key: Jurisdiction.PK,
    imageSrc: '/images/flags/pk-flag.svg',
    imageAlt: 'Flag of Pakistan',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'pkCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/pakistan.svg',
      imageAlt: 'Ice cream with the flag of Pakistan',
      isIceCream: true,
    },
    slug: 'pakistan',
  },
  PL: {
    key: Jurisdiction.PL,
    imageSrc: '/images/flags/pl-flag.svg',
    imageAlt: 'Flag of Poland',
    tooltipText: 'offering-card-tooltip-text-2-3',
    shortTooltipText: 'offering-card-tooltip-text-2-3-short',
    dataTest: 'plCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/poland.svg',
      imageAlt: 'Ice cream with the flag of Poland',
      isIceCream: true,
    },
    slug: 'poland',
  },
  PT: {
    key: Jurisdiction.PT,
    imageSrc: '/images/flags/pt-flag.svg',
    imageAlt: 'Flag of Portugal',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'ptCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/portugal.svg',
      imageAlt: 'Ice cream with the flag of Portugal',
      isIceCream: true,
    },
    slug: 'portugal',
  },
  PY: {
    key: Jurisdiction.PY,
    imageSrc: '/images/flags/py-flag.svg',
    imageAlt: 'Flag of Paraguay',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'pyCountryCard',
    slug: 'paraguay',
  },
  QA: {
    key: Jurisdiction.QA,
    imageSrc: '/images/flags/qa-flag.svg',
    imageAlt: 'Flag of Qatar',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'qaCountryCard',
    slug: 'qatar',
  },
  RO: {
    key: Jurisdiction.RO,
    imageSrc: '/images/flags/ro-flag.svg',
    imageAlt: 'Flag of Romania',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'roCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/romania.svg',
      imageAlt: 'Ice cream with the flag of Romania',
      isIceCream: true,
    },
    slug: 'romania',
  },
  SA: {
    key: Jurisdiction.SA,
    imageSrc: '/images/flags/sa-flag.svg',
    imageAlt: 'Flag of Saudi Arabia',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'saCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/saudi-arabia.svg',
      imageAlt: 'Ice cream with the flag of Saudi Arabia',
      isIceCream: true,
    },
    slug: 'saudi-arabia',
  },
  SE: {
    key: Jurisdiction.SE,
    imageSrc: '/images/flags/se-flag.svg',
    imageAlt: 'Flag of Sweden',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'seCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/sweden.svg',
      imageAlt: 'Ice cream with the flag of Sweden',
      isIceCream: true,
    },
    slug: 'sweden',
  },
  SG: {
    key: Jurisdiction.SG,
    imageSrc: '/images/flags/sg-flag.svg',
    imageAlt: 'Flag of Singapore',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'sgCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/singapore.svg',
      imageAlt: 'Ice cream with the flag of Singapore',
      isIceCream: true,
    },
    slug: 'singapore',
  },
  SI: {
    key: Jurisdiction.SI,
    imageSrc: '/images/flags/si-flag.svg',
    imageAlt: 'Flag of Slovenia',
    tooltipText: 'offering-card-tooltip-text-2-4',
    dataTest: 'siCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/slovenia.svg',
      imageAlt: 'Ice cream with the flag of Slovenia',
      isIceCream: true,
    },
    slug: 'slovenia',
  },
  SK: {
    key: Jurisdiction.SK,
    imageSrc: '/images/flags/sk-flag.svg',
    imageAlt: 'Flag of the Slovak Republic',
    tooltipText: 'offering-card-tooltip-text-2-4',
    shortTooltipText: 'offering-card-tooltip-text-2-4-short',
    dataTest: 'skCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/slovakia.svg',
      imageAlt: 'Ice cream with the flag of Slovakia',
      isIceCream: true,
    },
    slug: 'slovakia',
  },
  TH: {
    key: Jurisdiction.TH,
    imageSrc: '/images/flags/th-flag.svg',
    imageAlt: 'Flag of Thailand',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'thCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/thailand.svg',
      imageAlt: 'Ice cream with the flag of Thailand',
      isIceCream: true,
    },
    slug: 'thailand',
  },
  TJ: {
    key: Jurisdiction.TJ,
    imageSrc: '/images/flags/tj-flag.svg',
    imageAlt: 'Flag of Tajikistan',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'tjCountryCard',
    slug: 'tajikistan',
  },
  TM: {
    key: Jurisdiction.TM,
    imageSrc: '/images/flags/tm-flag.svg',
    imageAlt: 'Flag of Turkmenistan',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'tmCountryCard',
    slug: 'turkmenistan',
  },
  TN: {
    key: Jurisdiction.TN,
    imageSrc: '/images/flags/tn-flag.svg',
    imageAlt: 'Flag of Tunisia',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'tnCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/tunisia.svg',
      imageAlt: 'Ice cream with the flag of Tunisia',
      isIceCream: true,
    },
    slug: 'tunisia',
  },
  TR: {
    key: Jurisdiction.TR,
    imageSrc: '/images/flags/tr-flag.svg',
    imageAlt: 'Flag of Turkey',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'trCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/turkey.svg',
      imageAlt: 'Ice cream with the flag of Turkey',
      isIceCream: true,
    },
    slug: 'turkey',
  },
  TZ: {
    key: Jurisdiction.TZ,
    imageSrc: '/images/flags/tz-flag.svg',
    imageAlt: 'Flag of Tanzania',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'tzCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/tanzania.svg',
      imageAlt: 'Ice cream with the flag of Tanzania',
      isIceCream: true,
    },
    slug: 'tanzania',
  },
  TW: {
    key: Jurisdiction.TW,
    imageSrc: '/images/flags/tw-flag.svg',
    imageAlt: 'Flag of Taiwan',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'twCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/taiwan.svg',
      imageAlt: 'Ice cream with the flag of Taiwan',
      isIceCream: true,
    },
    slug: 'taiwan',
  },
  UA: {
    key: Jurisdiction.UA,
    imageSrc: '/images/flags/ua-flag.svg',
    imageAlt: 'Flag of Ukraine',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'uaCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/ukraine.svg',
      imageAlt: 'Ice cream with the flag of Ukraine',
      isIceCream: true,
    },
    slug: 'ukraine',
  },
  UK: {
    key: Jurisdiction.UK,
    imageSrc: '/images/flags/uk-flag.svg',
    imageAlt: 'Flag of the United Kingdom',
    tooltipText: 'offering-card-tooltip-text-2',
    renewalTooltip: 'offering-card-tooltip-text-2',
    dataTest: 'ukCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/london.svg',
      imageAlt: 'Big Ben with London Eye, United Kingdom',
    },
    slug: 'united-kingdom',
  },
  US: {
    key: Jurisdiction.US,
    imageSrc: '/images/flags/us-flag.svg',
    imageAlt: 'Flag of the United States of America',
    tooltipText: 'offering-card-tooltip-text-2',
    renewalTooltip: 'offering-card-tooltip-text-2',
    dataTest: 'usCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/new-york.svg',
      imageAlt: 'Statue of Liberty and New York city skyline, United States of America',
    },
    slug: 'united-states',
  },
  UY: {
    key: Jurisdiction.UY,
    imageSrc: '/images/flags/uy-flag.svg',
    imageAlt: 'Flag of Uruguay',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'uyCountryCard',
    slug: 'uruguay',
  },
  UZ: {
    key: Jurisdiction.UZ,
    imageSrc: '/images/flags/uz-flag.svg',
    imageAlt: 'Flag of Uzbekistan',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'uzCountryCard',
    slug: 'uzbekistan',
  },
  VE: {
    key: Jurisdiction.VE,
    imageSrc: '/images/flags/ve-flag.svg',
    imageAlt: 'Flag of Venezuela',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'veCountryCard',
    slug: 'venezuela',
  },
  VN: {
    key: Jurisdiction.VN,
    imageSrc: '/images/flags/vn-flag.svg',
    imageAlt: 'Flag of Vietnam',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'vnCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/vietnam.svg',
      imageAlt: 'Ice cream with the flag of Vietnam',
      isIceCream: true,
    },
    slug: 'vietnam',
  },
  ZA: {
    key: Jurisdiction.ZA,
    imageSrc: '/images/flags/za-flag.svg',
    imageAlt: 'Flag of South Africa',
    tooltipText: 'offering-card-tooltip-text-2',
    dataTest: 'zaCountryCard',
    landingPageData: {
      imageSrc: '/images/hero/country-specific/south-africa.svg',
      imageAlt: 'Ice cream with the flag of South Africa',
      isIceCream: true,
    },
    slug: 'south-africa',
  },
}

const regionalCountryList = jurisdictionsData()

export const getRegionalCountryList = (
  from: Region,
  to: Region | null,
  numOfCountries?: number,
) => {
  const fromCountries = regionalCountryList[from].list
  const toCountries = to ? regionalCountryList[to].list : []
  const [first, second, ...rest] = fromCountries
  return _.take(_.uniq([first, second, ...toCountries, ...rest]), numOfCountries ?? 9)
}

export const getCountryCards = (
  language: Locale,
  from: Region | null,
  to: Region | null,
  numOfCountries?: number,
  sorted?: boolean,
  allowedJurisdictions?: Jurisdiction[],
): CountryCard[] => {
  const collator = new Intl.Collator(language, {numeric: false})
  const jurisdictions = from
    ? getRegionalCountryList(from, to, numOfCountries)
    : getJurisdictionsAlpha2()
  const list = allowedJurisdictions
    ? jurisdictions.filter((jurisdiction) => allowedJurisdictions.includes(jurisdiction))
    : jurisdictions

  const cards = list.map((country) => ({
    ...countryCards[country],
    title: alpha2ToName(country, language),
  }))

  return sorted ? cards.sort((a, b) => collator.compare(a.title, b.title)) : cards
}

export const filterEuCountries = (countries: Jurisdiction[]): Jurisdiction[] =>
  countries.filter((country: Jurisdiction) => !euCountriesCodes.includes(country))

export const isIncludedInEu = (country: Jurisdiction, selectedCountries: Jurisdiction[]): boolean =>
  euCountriesCodes.includes(country) && selectedCountries.includes(Jurisdiction.EU)

const allCountryLandingPages = Object.values(countryCards).filter(
  ({landingPageData}) => !!landingPageData,
)

export const getCountryLandingPageData = (country: Jurisdiction) => {
  const countryLandingPage = allCountryLandingPages.find(({key}) => key === country)
  return countryLandingPage?.landingPageData
}

// Resolves a route slug to its Jurisdiction, optionally filtered by an extra check.
export const findJurisdictionBySlug = (
  slug: string,
  isValid: (card: CountryCardContent) => boolean = (card) => Boolean(card.landingPageData),
): Jurisdiction | null => {
  const countryCard = Object.values(countryCards).find((card) => card.slug === slug)
  return countryCard && isValid(countryCard) ? countryCard.key : null
}
